import React, { useMemo } from 'react'
import { RealTimeWrapper, Show } from 'components';

import { BellAlertIcon } from '@heroicons/react/24/outline';
import Push from "push.js";
import clsx from 'clsx';
import config from 'config';
import { useKavaNotifications } from 'hooks/data/use-kava-notifications';

type Props = {
    isCollapsed: boolean;
    setOpen: (val: boolean) => void;
}

const KavaNotifications = ({ isCollapsed, setOpen }: Props) => {
    const { kavaNotifications, refetch } =
        useKavaNotifications();

    const getTotalThreadNotifications = useMemo(() => {
        return kavaNotifications.reduce((total, notification) => {
            return total + (notification?.status === "UNREAD" ? 1 : 0);
        }, 0);
    }, [kavaNotifications]);


    const playBeep = () => {
        const context = new (window.AudioContext)();
        const oscillator = context.createOscillator();
        const gain = context.createGain();

        oscillator.type = "square"; // you can also use 'sine', 'triangle', 'sawtooth'
        oscillator.frequency.setValueAtTime(440, context.currentTime); // A4 = 440 Hz

        oscillator.connect(gain);
        gain.connect(context.destination);

        oscillator.start();
        setTimeout(() => {
            oscillator.stop();
            context.close();
        }, 200); // 200 ms beep
    };

    const onMessageReceived = (message: any) => {
        try {
            // playBeep();
            Push.create("Visal-Re <> KAVA Notification", {
                body: message.message,
                icon: require("assets/logo.png"),
                // timeout: 4000,
                onClick: function () {
                    setOpen(true);
                },
            });
            refetch();
        } catch (e) { }
    }


    const onGranted = () => {
        Push.create("Visal-Re <> KAVA Notification", {
            body: "Welcome to Visal Reinsurance From Kava",
            icon: require("assets/logo.png"),
            timeout: 4000,
            onClick: () => {
                window.focus();
            },
        });
    };
    const onDenied = () => { };

    React.useEffect(() => {
        if (!Push.Permission.has()) {
            Push.Permission.request(onGranted, onDenied);
            Push.Permission.get();
        }
    }, []);

    return (
        <RealTimeWrapper channel={config.pusher.channels.kava} events={["reinsurer.thread_notification", "reinsurer.read_thread", "reinsurer.remove_interest"]} callBack={onMessageReceived}>
            <Show if={getTotalThreadNotifications > 0}>
                <button
                    onClick={() => {
                        setOpen(true);
                    }}
                    className={clsx(
                        "group flex items-center w-full px-2 py-2 text-sm rounded-md text-gray-300 hover:bg-sidebar-800 hover:text-white relative", // Added w-full for consistent layout
                        isCollapsed && "justify-center"
                    )}
                    title="Kava Notifications"
                >
                    {/* Icon and Badge Container */}
                    <div className="relative flex-shrink-0">
                        <BellAlertIcon
                            className={clsx(
                                "text-gray-200 group-hover:text-gray-50 h-6 w-6"
                                // Margin is handled by the text span container below when expanded
                            )}
                            aria-hidden="true"
                        />
                        {/* Badge for Collapsed State (positioned relative to icon) */}
                        {isCollapsed && getTotalThreadNotifications > 0 && (
                            <span
                                className="absolute -top-1 -right-1 inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold leading-none text-red-100 bg-red-600 rounded-full transform translate-x-1/4 -translate-y-1/4"
                                aria-label={`${getTotalThreadNotifications} Kava notifications`}
                            >
                                {/* Optionally cap the displayed count */}
                                {getTotalThreadNotifications > 9 ? '9+' : getTotalThreadNotifications}
                            </span>
                        )}
                    </div>

                    {/* Text and Badge for Expanded State */}
                    {!isCollapsed && (
                        <span className="flex-1 flex justify-between items-center ml-3"> {/* Added ml-3 for spacing from icon */}
                            <span>Kava Notifications</span>
                            {/* Badge for Expanded State (aligned to the right) */}
                            {getTotalThreadNotifications > 0 && (
                                <span
                                    className="inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-red-100 bg-red-600 rounded-full"
                                    aria-label={`${getTotalThreadNotifications} Kava notifications`}
                                >
                                    {getTotalThreadNotifications}
                                </span>
                            )}
                        </span>
                    )}

                    {/* Tooltip for Collapsed State */}
                    {isCollapsed && (
                        <span className="absolute left-full ml-2 w-auto min-w-max rounded bg-black text-xs text-white p-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10">
                            Kava Notifications
                        </span>
                    )}
                </button>
            </Show >
        </RealTimeWrapper>
    )
}

export default KavaNotifications;